/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { memo, useCallback, useEffect, useState } from 'react';
import DropdownTreeSelect, {
  TreeData,
  TreeNode
} from 'react-dropdown-tree-select';
import {
  getSurveyAssurancePortfolioQuestions,
  updateSurveyAssurancePortfolioQuestions
} from '../../actions/assurance';
import Loader from '../loader';
import {
  AssuranceQuestion,
  convertQuestionGroupToTree,
  extractUtrIds,
  extractUtrvIds, IdsContainer
} from './questionGroup';
import './TreeCheckbox.scss';
import { SurveyActionData } from '../../model/surveyData';
import { getSurveyGroupsSelector } from '../../selectors/survey';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { Button } from 'reactstrap';
import { useAppSelector } from '../../reducers';
import { QUESTION } from '../../constants/terminology';

interface DropdownTreeProps {
  questions: TreeData,
  onChange: (currentNode: TreeNode, selectedNodes: TreeNode[]) => void,
}

const DropdownTreeSelectMemo = memo(({ questions, onChange }: DropdownTreeProps) => {
  return <DropdownTreeSelect
    data={questions}
    onChange={onChange}
    showPartiallySelected={true}
    keepTreeOnSearch={true}
    keepChildrenOnSearch={true}
    showDropdown={'always'}
    texts={{
      placeholder: `SEARCH ${QUESTION.UPPERCASE_PLURAL}`,
    }}
  />
});

export interface TreeCheckboxProps {
  toggle: () => void;
  initiativeId: string;
  portfolioId: string;
  surveyId: string;
  afterSubmit: () => void
}

interface ModalState {
  loaded: boolean;
  data: {
    survey?: SurveyActionData;
    currentIds: string[];
  };
  questions: AssuranceQuestion[];
  message: string;
  errored: boolean;
}

interface getSurveyAssurancePortfolioQuestionsResponse {
  survey: SurveyActionData;
  current: string[];
}

export const TreeCheckbox = ({ portfolioId, surveyId, initiativeId, toggle, afterSubmit }: TreeCheckboxProps) => {

  const [nodes, setNodes] = useState<IdsContainer[]|null>(null);
  const [modalState, setData] = useState<ModalState>({
    loaded: false,
    data: {
      currentIds: [],
    },
    questions: [],
    message: '',
    errored: false
  });

  const originalQuestionList = useAppSelector(getSurveyGroupsSelector);

  const onChange = useCallback((currentNode, selectedNodes) => {
    setNodes(selectedNodes);
  }, []);

  const handleSubmit = () => {
    if (nodes === null || !modalState.data.survey) {
      // Nothing changed
      return toggle();
    }

    setData((m) => ({ ...m, loaded: false }));
    // Extract utrCodes;
    const utrvIds = extractUtrvIds(modalState.data.survey, nodes);

    updateSurveyAssurancePortfolioQuestions(portfolioId, { questions: utrvIds })
      .then(() => setData((m) => ({ ...m, loaded: true })))
      .then(() => {
        const analytics = getAnalytics();
        return analytics.track(AnalyticsEvents.SurveyAssuranceDatapointUpdated, {
          initiativeId,
          portfolioId,
          surveyId
        });
      })
      .then(() => afterSubmit())
      .catch((e: Error) => setData((m) => ({
        ...m,
        errored: true,
        message: e.message
      })));
  };

  useEffect(() => {
    getSurveyAssurancePortfolioQuestions(portfolioId)
      .then((data: getSurveyAssurancePortfolioQuestionsResponse) => {
        const utrIds = extractUtrIds(data.survey, data.current);
        const questions = convertQuestionGroupToTree(originalQuestionList, utrIds);
        setData((m) => ({
          ...m,
          loaded: true,
          data: { ...m.data, ...data },
          questions: questions
        }));
      })
      .catch((e: Error) => setData((m) => ({ ...m, errored: true, message: e.message })));
  }, [portfolioId, surveyId, originalQuestionList]);

  if (modalState.errored) {
    return <div>{modalState.message}</div>
  }

  if (!modalState.loaded) {
    return <Loader />
  }

  return (
    <div className={'tree-dropdown-grid'}>
      <DropdownTreeSelectMemo questions={modalState.questions} onChange={onChange} />
      <div className='text-right mt-4'>
        <Button color='link' className='mr-3'
          onClick={toggle}>Cancel
        </Button>
        <Button color='primary' onClick={handleSubmit}>
          Update
        </Button>
      </div>
    </div>
  );
};
